import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  GripVertical,
  Settings,
  Eye
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' }
];

const FormBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState('custom');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      options: [],
      placeholder: ''
    };
    setFields([...fields, newField]);
    setEditingField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setFields(fields.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  };

  const removeField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (editingField === fieldId) {
      setEditingField(null);
    }
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      toast.error("Please enter a form title");
      return;
    }
    if (fields.length === 0) {
      toast.error("Please add at least one field");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/forms/templates/custom`, {
        form_type: formType,
        title: formTitle,
        description: formDescription,
        fields: fields.map(f => ({
          id: f.id,
          label: f.label,
          type: f.type,
          required: f.required,
          options: f.options?.length > 0 ? f.options : undefined,
          placeholder: f.placeholder || undefined
        }))
      }, { withCredentials: true });
      
      toast.success("Form template created successfully!");
      navigate('/forms');
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error(error.response?.data?.detail || "Failed to save form");
    } finally {
      setSaving(false);
    }
  };

  const currentField = fields.find(f => f.id === editingField);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="form-builder-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/forms">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-bold text-primary">Form Builder</h1>
              <p className="text-muted-foreground">Create custom safety forms</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="preview-btn">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-form-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Form Settings */}
          <div className="lg:col-span-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Form Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="formTitle">Form Title</Label>
                  <Input
                    id="formTitle"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="e.g., Daily Safety Checklist"
                    data-testid="form-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="formDescription">Description</Label>
                  <Textarea
                    id="formDescription"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Brief description of this form..."
                    data-testid="form-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="formType">Form Category</Label>
                  <Select value={formType} onValueChange={setFormType}>
                    <SelectTrigger data-testid="form-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom Form</SelectItem>
                      <SelectItem value="incident">Incident Report</SelectItem>
                      <SelectItem value="equipment_inspection">Equipment Inspection</SelectItem>
                      <SelectItem value="job_hazard_analysis">Job Hazard Analysis</SelectItem>
                      <SelectItem value="jobsite_inspection">Jobsite Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Field Editor */}
            {currentField && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Field Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Label</Label>
                    <Input
                      value={currentField.label}
                      onChange={(e) => updateField(currentField.id, { label: e.target.value })}
                      data-testid="field-label-input"
                    />
                  </div>
                  <div>
                    <Label>Field Type</Label>
                    <Select 
                      value={currentField.type} 
                      onValueChange={(v) => updateField(currentField.id, { type: v })}
                    >
                      <SelectTrigger data-testid="field-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(currentField.type === 'select' || currentField.type === 'multiselect') && (
                    <div>
                      <Label>Options (one per line)</Label>
                      <Textarea
                        value={(currentField.options || []).join('\n')}
                        onChange={(e) => updateField(currentField.id, { 
                          options: e.target.value.split('\n').filter(o => o.trim()) 
                        })}
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                        data-testid="field-options-input"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Placeholder</Label>
                    <Input
                      value={currentField.placeholder || ''}
                      onChange={(e) => updateField(currentField.id, { placeholder: e.target.value })}
                      data-testid="field-placeholder-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="required"
                      checked={currentField.required}
                      onCheckedChange={(c) => updateField(currentField.id, { required: c })}
                      data-testid="field-required-checkbox"
                    />
                    <Label htmlFor="required" className="font-normal cursor-pointer">Required field</Label>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Fields List */}
          <div className="lg:col-span-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Form Fields</CardTitle>
                <Button onClick={addField} data-testid="add-field-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </CardHeader>
              <CardContent>
                {fields.length > 0 ? (
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div 
                        key={field.id}
                        className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                          editingField === field.id ? 'border-accent bg-accent/5' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setEditingField(field.id)}
                        data-testid={`field-item-${index}`}
                      >
                        <GripVertical className="w-5 h-5 text-muted-foreground cursor-move" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-primary">{field.label}</span>
                            {field.required && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground capitalize">{field.type.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); moveField(index, 'up'); }}
                            disabled={index === 0}
                          >
                            ↑
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); moveField(index, 'down'); }}
                            disabled={index === fields.length - 1}
                          >
                            ↓
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-field-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No fields added yet</p>
                    <p className="text-sm">Click "Add Field" to start building your form</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{formTitle || 'Form Preview'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {formDescription && (
                <p className="text-muted-foreground">{formDescription}</p>
              )}
              {fields.map((field, index) => (
                <div key={field.id}>
                  <Label>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  <div className="mt-1.5">
                    {field.type === 'textarea' ? (
                      <Textarea placeholder={field.placeholder} disabled />
                    ) : field.type === 'select' ? (
                      <Select disabled>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      </Select>
                    ) : field.type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <Checkbox disabled />
                        <Label className="font-normal">Yes</Label>
                      </div>
                    ) : (
                      <Input type={field.type} placeholder={field.placeholder} disabled />
                    )}
                  </div>
                </div>
              ))}
              {fields.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Add fields to see a preview
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FormBuilder;
